import loggerService from './loggerService';

/**
 * @description Finish a performance transaction
 * @param {object} transaction - Transaction object returned from startTransaction
 * @param {string} [status] - Final transaction status (e.g., 'ok', 'error')
 */
const finishTransaction = (transaction, status) => {
  if (!transaction) return;

  const { sentryTransaction, startTime, name, tags, data, op } = transaction;

  if (typeof window.Sentry !== 'undefined') {
    if (!sentryTransaction) {
      const error = new Error(
        `Sentry transaction is null for transaction "${name}".`,
      );
      loggerService.error(error, {
        context: {
          transactionName: name,
          op,
          status,
          tags,
        },
      });

      return;
    }

    try {
      if (status) sentryTransaction.setStatus(status);
      sentryTransaction.finish();
    } catch (error) {
      loggerService.error(error, {
        context: {
          transactionName: name,
          status,
        },
      });
    }
  }

  if (process.env.NODE_ENV === 'development') {
    const duration = Date.now() - startTime;
    console.log(`[Transaction] ${name} finished in ${duration}ms`, {
      op,
      status,
      tags,
      data,
    });
  }
};

/**
 * @description Start a performance transaction
 * @param {object} options - Transaction options
 * @param {string} options.name - Transaction name (required)
 * @param {string} [options.module] - Module name (will be added as 'module' tag)
 * @param {string} [options.op] - Operation type (e.g., 'ui.action', 'http.server', 'db.query')
 * @param {object} [options.tags={}] - Tags for filtering/grouping
 * @param {object} [options.data={}] - Detailed context data
 * @returns {object|null} Transaction object, or null if name is missing
 * @deprecated This function uses Sentry.startTransaction() which is deprecated in v7 and removed in v8.
 *             When upgrading Sentry to v8+, migrate to the new span-based API.
 */
const startTransaction = ({ name, module, op, tags = {}, data = {} }) => {
  if (!name) {
    return null;
  }

  const startTime = Date.now();
  const mergedTags = module ? { ...tags, module } : tags;
  let sentryTransaction = null;

  try {
    if (typeof window.Sentry !== 'undefined') {
      const sentryContext = {
        name,
        tags: mergedTags,
        data,
        ...(op && { op }),
      };
      // NOTE: Sentry.startTransaction() has been deprecated and removed in v8.
      // In v8, Sentry replaced the transaction-based API with a span-based model
      // to align with OpenTelemetry standards. If upgrading to v8+, this code
      // needs to be migrated to use the new span-based APIs.
      // Reference: https://docs.sentry.io/platforms/javascript/migration/v7-to-v8/
      sentryTransaction = window.Sentry.startTransaction(sentryContext);
    }
  } catch (error) {
    loggerService.error(error, {
      context: {
        transactionName: name,
        module,
        op,
      },
    });
  }

  const transaction = {
    op,
    name,
    data,
    startTime,
    tags: mergedTags,
    sentryTransaction,
    finish: (status) => {
      finishTransaction(transaction, status);
    },
  };

  return transaction;
};

export { startTransaction, finishTransaction };
